import React, { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';

import { useMerchant } from '@contexts/admin/MerchantContext';

import { adminApiFetch } from '@services/admin/apiService';

interface MerchantDataItem {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface MerchantDataContextValue {
  salesChannels: MerchantDataItem[];
  shippingTypes: MerchantDataItem[];
  inventoryTypes: MerchantDataItem[];
  shelfTypes: MerchantDataItem[];
  loading: boolean;
  error: string | null;
  refreshMerchantData: () => void;
  refreshSalesChannels: () => Promise<MerchantDataItem[]>;
  refreshInventoryTypes: () => Promise<MerchantDataItem[]>;
}

const MerchantDataContext = createContext<MerchantDataContextValue | undefined>(undefined);

interface MerchantDataProviderProps {
  children: ReactNode;
}

export const MerchantDataProvider: React.FC<MerchantDataProviderProps> = ({ children }) => {
  // useMerchant comes from a .jsx context; pull only the field we need.
  const { currentMerchant } = useMerchant() as { currentMerchant: { id?: string } | null };
  const initRef = useRef(false);

  // 狀態管理
  const [salesChannels, setSalesChannels] = useState<MerchantDataItem[]>([]);
  const [shippingTypes, setShippingTypes] = useState<MerchantDataItem[]>([]);
  const [inventoryTypes, setInventoryTypes] = useState<MerchantDataItem[]>([]);
  const [shelfTypes, setShelfTypes] = useState<MerchantDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 獲取 merchant channels
  const fetchSalesChannels = async (merchantId?: string): Promise<MerchantDataItem[]> => {
    try {
      const response = await adminApiFetch(`/sales_channels.json`);
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Failed to fetch merchant channels:', err);
      throw err;
    }
  };

  // 獲取 shipment types
  const fetchShippingTypes = async (): Promise<MerchantDataItem[]> => {
    try {
      const response = await adminApiFetch('/shipping_types.json');
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Failed to fetch shipment types:', err);
      throw err;
    }
  };

  // 獲取 inventory types
  const fetchInventoryTypes = async (merchantId?: string): Promise<MerchantDataItem[]> => {
    try {
      const response = await adminApiFetch(`/inventory_types.json`);
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Failed to fetch inventory types:', err);
      throw err;
    }
  };

  // 獲取 shelf types
  const fetchShelfTypes = async (): Promise<MerchantDataItem[]> => {
    try {
      const response = await adminApiFetch('/shelf_types.json');
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Failed to fetch shelf types:', err);
      throw err;
    }
  };

  // 載入所有資料
  const loadMerchantData = async (merchantId?: string) => {
    if (!merchantId) {
      setSalesChannels([]);
      setShippingTypes([]);
      setInventoryTypes([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 並行請求所有資料
      const [channels, shippingTypesData, inventoryTypesData] = await Promise.all([
        fetchSalesChannels(merchantId),
        fetchShippingTypes(),
        fetchInventoryTypes(merchantId)
      ]);

      setSalesChannels(channels);
      setShippingTypes(shippingTypesData);
      setInventoryTypes(inventoryTypesData);
    } catch (err) {
      console.error('Failed to load merchant data:', err);
      setError(err instanceof Error ? err.message : String(err));
      setSalesChannels([]);
      setShippingTypes([]);
      setInventoryTypes([]);
    } finally {
      setLoading(false);
    }
  };

  // 當 currentMerchant 變更時重新載入資料
  useEffect(() => {
    if (currentMerchant?.id) {
      loadMerchantData(currentMerchant.id);
    } else {
      setSalesChannels([]);
      setShippingTypes([]);
      setInventoryTypes([]);
      setLoading(false);
      setError(null);
    }
  }, [currentMerchant?.id]);

  // 重新載入資料的方法
  const refreshMerchantData = () => {
    if (currentMerchant?.id) {
      loadMerchantData(currentMerchant.id);
    }
  };

  // 重新載入銷售通路（不觸發全域 loading，避免表單卸載）
  const refreshSalesChannels = async (): Promise<MerchantDataItem[]> => {
    if (!currentMerchant?.id) return [];
    const data = await fetchSalesChannels(currentMerchant.id);
    setSalesChannels(data);
    return data;
  };

  // 重新載入庫存類型並回傳結果
  const refreshInventoryTypes = async (): Promise<MerchantDataItem[]> => {
    if (!currentMerchant?.id) return [];
    const data = await fetchInventoryTypes(currentMerchant.id);
    setInventoryTypes(data);
    return data;
  };

  const value: MerchantDataContextValue = {
    salesChannels,
    shippingTypes,
    inventoryTypes,
    loading,
    error,
    shelfTypes,
    refreshMerchantData,
    refreshSalesChannels,
    refreshInventoryTypes,
  };

  return (
    <MerchantDataContext.Provider value={value}>
      {children}
    </MerchantDataContext.Provider>
  );
};

export const useMerchantData = (): MerchantDataContextValue | null => {
  const context = useContext(MerchantDataContext);
  // Return null when called outside MerchantDataProvider (e.g., in Operator context)
  // This allows shared components to be used in both admin and operator
  return context || null;
};

export default MerchantDataContext;
